import React, { forwardRef, memo, useImperativeHandle, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useStyles } from '../style';
import { getRunTimeEnv } from '../../../util/getEnvVariable';

/**
 * @name LoginDetails - Login details component
 * @param {string} errors - Errors returned on input field validation
 * @function onChange  - Event handling function on field input
 * @param {object} ref-References account details field
 * @returns {JSX.Element} - Login details component
 */
const LoginDetails = forwardRef(({ errors, onChange, values, onBlur }, ref) => {
  const theme = useTheme();
  const style = useStyles(theme);
  const sm = useMediaQuery(theme.breakpoints.down('sm'));
  const [showPassword, setShowPassword] = useState(false);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const clientSideEnv = getRunTimeEnv();

  /**
   * @name handleClickShowPassword
   * @description - Function to toggle the visibility of password
   * @returns {boolean} - Hides or shows password
   */
  const handleClickShowPassword = () => setShowPassword(show => !show);

  /**
   * @name handleMouseDownPassword
   * @description - Function to handle MouseDown event on password field
   * @param {Function} event - Event can be something the browser does, or something a user does
   */
  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  /**
   * @name useImperativeHandle
   * @description - customizes the instance value i.e exposed to parent components when using ref
   * @param {string} ref - References text fields
   * @returns {string} -Returns ref value
   */
  useImperativeHandle(
    ref,
    () => ({
      /**
       * @name focusEmail
       * @description Function to focus account Number field
       * @returns {Function} - Focuses current ref value
       */
      focusEmail: () => emailRef.current?.focus(),
      /**
       * @name focusPassword
       * @description Function to focus landline Number field
       * @returns {Function} - Focuses current ref value
       */
      focusPassword: () => passwordRef.current?.focus(),
    }),
    [],
  );

  return (
    <Box>
      {sm && <Divider sx={style.divider} />}
      <Stack spacing={4} sx={style.fields}>
        <FormControl variant='outlined' error={!!errors.email}>
          <TextField
            name='email'
            label='Email'
            variant='outlined'
            sx={style.inputTextbox}
            inputRef={emailRef}
            onChange={onChange}
            helperText={errors.email}
            value={values.email}
            onBlur={onBlur}
          />
          <Typography sx={style.forgotLink} variant='helperText'>
            <Link
              href={`${clientSideEnv.NEXT_PUBLIC_MY_EIR}/forgot-email`}
              target='_blank'
              sx={style.link}
              aria-label='Forgot email?'
            >
              Forgot email?
            </Link>
          </Typography>
        </FormControl>

        <FormControl variant='outlined' sx={style.inputTextbox} error={!!errors.password}>
          <TextField
            name='password'
            label='Password'
            variant='outlined'
            type={showPassword ? 'text' : 'password'}
            sx={style.inputTextbox}
            inputRef={passwordRef}
            onChange={onChange}
            onBlur={onBlur}
            value={values.password}
            helperText={errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle password visibility'
                    disableFocusRipple
                    disableRipple
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge='end'
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Typography sx={style.forgotLink} variant='helperText'>
            <Link
              href={`${clientSideEnv.NEXT_PUBLIC_MY_EIR}/forgot-password`}
              target='_blank'
              sx={style.link}
              aria-label='Forgot password?'
            >
              Forgot password?
            </Link>
          </Typography>
        </FormControl>
      </Stack>
    </Box>
  );
});

LoginDetails.displayName = 'LoginDetails';
export default memo(LoginDetails);
